/**
 * useDocListData Hook
 *
 * Manages data fetching from Firebase functions with pagination support.
 * Handles loading states, errors, and cursor-based pagination.
 * Implements progressive batch loading and document cap enforcement.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebaseFunctions';
import type { BaseListDocument, DataSourceConfig } from './DocList.types';
import {
  DOC_LIST_INITIAL_BATCH_SIZE,
  DOC_LIST_SUBSEQUENT_BATCH_SIZE,
  DOC_LIST_MAX_TOTAL_DOCUMENTS,
} from '#types/System.Parameters.types';

interface UseDocListDataProps<TDoc extends BaseListDocument, TRequest, TResponse> {
  dataSource: DataSourceConfig<TDoc, TRequest, TResponse>;
  queryParams: Record<string, unknown>;
  enabled?: boolean;
  onError?: (error: Error) => void;
}

interface UseDocListDataReturn<TDoc extends BaseListDocument> {
  data: TDoc[];
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  error: Error | null;
  batchCount: number;
  queryTotalCount: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  aggregateResult: { type: 'count' | 'sum' | 'avg'; value: number } | null;
  isAtDocumentCap: boolean;
  /** Increments whenever the base query is (re)run (initial load or refresh), but not on loadMore. */
  queryRunId: number;
}

export function useDocListData<TDoc extends BaseListDocument, TRequest, TResponse>({
  dataSource,
  queryParams,
  enabled = true,
  onError,
}: UseDocListDataProps<TDoc, TRequest, TResponse>): UseDocListDataReturn<TDoc> {
  const [data, setData] = useState<TDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [batchCount, setBatchCount] = useState(0);
  const [queryTotalCount, setQueryTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [aggregateResult, setAggregateResult] = useState<{ type: 'count' | 'sum' | 'avg'; value: number } | null>(null);
  const [queryRunId, setQueryRunId] = useState(0);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const isMountedRef = useRef(true);
  const dataLengthRef = useRef(0);
  const loadingRef = useRef(false);
  const activeRequestIdRef = useRef(0);
  const activeQueryKeyRef = useRef('');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async (cursor: string | null = null, append: boolean = false) => {
      if (!enabled) return;

      const queryKey = `${dataSource.functionName}|${JSON.stringify(queryParams)}`;
      const isNewQuery = activeQueryKeyRef.current !== queryKey;
      const isActiveQueryInFlight = loadingRef.current && !isNewQuery;
      if (isActiveQueryInFlight) return;

      if (isNewQuery && !append) {
        activeQueryKeyRef.current = queryKey;
        dataLengthRef.current = 0;
        setData([]);
        setBatchCount(0);
        setQueryTotalCount(0);
        setHasMore(false);
        setNextCursor(null);
        setAggregateResult(null);
      }

      // Check document cap before fetching
      if (append && dataLengthRef.current >= DOC_LIST_MAX_TOTAL_DOCUMENTS) {
        console.warn(`Document cap reached: ${DOC_LIST_MAX_TOTAL_DOCUMENTS}. Cannot load more documents.`);
        return;
      }

      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      try {
        loadingRef.current = true;

        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        setIsError(false);
        setError(null);

        const callable = httpsCallable<TRequest, TResponse>(
          functions,
          dataSource.functionName
        );

        // Use progressive batch sizes: smaller initial, larger subsequent
        const batchSize = cursor === null ? DOC_LIST_INITIAL_BATCH_SIZE : DOC_LIST_SUBSEQUENT_BATCH_SIZE;
        const request = dataSource.buildRequest(queryParams, cursor, batchSize);
        const result = await callable(request);
        const parsed = dataSource.parseResponse(result.data);

        if (!isMountedRef.current) return;
        if (activeRequestIdRef.current !== requestId) return;

        // Enforce document cap when appending
        if (append) {
          const currentLength = dataLengthRef.current;
          const remainingCapacity = DOC_LIST_MAX_TOTAL_DOCUMENTS - currentLength;
          const documentsToAdd = parsed.documents.slice(0, remainingCapacity);

          const isCapped = documentsToAdd.length < parsed.documents.length;
          if (isCapped) {
            console.warn(
              `Document cap reached: ${DOC_LIST_MAX_TOTAL_DOCUMENTS}. ` +
              `Only adding ${documentsToAdd.length} of ${parsed.documents.length} documents.`
            );
          }

          const nextLength = currentLength + documentsToAdd.length;

          setData((prev) => {
            const next = [...prev, ...documentsToAdd];
            dataLengthRef.current = next.length;
            return next;
          });

          setHasMore(!isCapped && parsed.hasMore && nextLength < DOC_LIST_MAX_TOTAL_DOCUMENTS);
          setNextCursor(!isCapped ? parsed.nextCursor : null);
        } else {
          // For initial load, still respect cap but allow up to initial batch size
          const documentsToSet = parsed.documents.slice(0, DOC_LIST_MAX_TOTAL_DOCUMENTS);

          const isCapped = documentsToSet.length < parsed.documents.length;

          setData(() => {
            dataLengthRef.current = documentsToSet.length;
            return documentsToSet;
          });

          setHasMore(!isCapped && parsed.hasMore && documentsToSet.length < DOC_LIST_MAX_TOTAL_DOCUMENTS);
          setNextCursor(!isCapped ? parsed.nextCursor : null);
        }

        setBatchCount(parsed.batchCount);
        setQueryTotalCount(parsed.queryTotalCount);
        
        // Store aggregate result if present
        if (parsed.aggregateResult) {
          setAggregateResult(parsed.aggregateResult);
        }

        if (!append) {
          setQueryRunId((prev) => prev + 1);
        }
      } catch (err) {
        if (!isMountedRef.current) return;

        const normalizedError = err instanceof Error ? err : new Error('Unknown error');
        console.error('Failed to load data', normalizedError);
        setIsError(true);
        setError(normalizedError);
        onErrorRef.current?.(normalizedError);
      } finally {
        if (isMountedRef.current && activeRequestIdRef.current === requestId) {
          setIsLoading(false);
          setIsLoadingMore(false);
          loadingRef.current = false;
        }
      }
    },
    [dataSource, queryParams, enabled]
  );

  const loadMore = useCallback(async () => {
    if (nextCursor && hasMore && !isLoadingMore) {
      await fetchData(nextCursor, true);
    }
  }, [nextCursor, hasMore, isLoadingMore, fetchData]);

  const refresh = useCallback(async () => {
    dataLengthRef.current = 0;
    setData([]);
    setNextCursor(null);
    setHasMore(false);
    setAggregateResult(null);
    await fetchData(null, false);
  }, [fetchData]);

  useEffect(() => {
    if (enabled) {
      void fetchData(null, false);
    }
  }, [enabled, fetchData]);

  const isAtDocumentCap = data.length >= DOC_LIST_MAX_TOTAL_DOCUMENTS;

  return {
    data,
    isLoading,
    isLoadingMore,
    isError,
    error,
    batchCount,
    queryTotalCount,
    hasMore,
    loadMore,
    refresh,
    aggregateResult,
    isAtDocumentCap,
    queryRunId,
  };
}

